import { useEffect, useState } from 'react';
import en from './en';
import fr from './fr';

type NestedKeys<T, Prefix extends string = ''> = T extends object
  ? {
      [K in keyof T & string]: T[K] extends object
        ? NestedKeys<T[K], `${Prefix}${K}.`>
        : `${Prefix}${K}`;
    }[keyof T & string]
  : never;

export type TranslationKey = NestedKeys<typeof en>;

export const SUPPORTED_LOCALES = ['en', 'fr'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

const dictionaries: Record<Locale, any> = { en, fr };
const STORAGE_KEY = 'urunn_locale';

const isSupported = (l: string): l is Locale => (SUPPORTED_LOCALES as readonly string[]).includes(l);

type LocaleSource = 'user' | 'device' | 'fallback';

// How `currentLocale` was chosen. Any real signal about this visitor — their own
// pick ('user') or their device's language ('device') — outranks a deal's default
// language. 'fallback' means we had no signal at all and just defaulted to English.
let source: LocaleSource = 'fallback';

function detectLocale(): Locale {
  try {
    // 1. ?lang= in the URL (e.g. linked from the top-level marketing site) wins
    //    and is treated as an explicit, persisted choice.
    const q = new URLSearchParams(window.location.search).get('lang');
    if (q && isSupported(q.toLowerCase())) {
      const loc = q.toLowerCase() as Locale;
      source = 'user';
      try {
        localStorage.setItem(STORAGE_KEY, loc);
      } catch {
        /* ignore */
      }
      return loc;
    }
    // 2. Previously saved explicit choice
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && isSupported(saved)) {
      source = 'user';
      return saved;
    }
  } catch {
    /* localStorage / URL unavailable */
  }
  // 3. Device language, else English
  const device = detectDeviceLocale();
  source = device ? 'device' : 'fallback';
  return device ?? 'en';
}

/**
 * First supported locale in the device's ordered language preferences
 * (Settings > Language on mobile, browser language settings on desktop).
 * Matches on the primary subtag, so `fr-CA` resolves to `fr`. Returns null when
 * the device asks for nothing we translate — the caller must be able to tell
 * that apart from a device that genuinely prefers English.
 */
function detectDeviceLocale(): Locale | null {
  if (typeof navigator === 'undefined') return null;
  const prefs = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const pref of prefs) {
    const primary = pref?.toLowerCase().split('-')[0];
    if (primary && isSupported(primary)) return primary;
  }
  return null;
}

/** Keep <html lang> in sync so screen readers and SEO see the right language. */
function syncDocumentLang(locale: Locale): void {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }
}

let currentLocale: Locale = detectLocale();
syncDocumentLang(currentLocale);

// Subscribers re-render when the locale changes (no page reload).
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

export function getLocale(): Locale {
  return currentLocale;
}

/** User-initiated locale change — persisted and treated as explicit. */
export function setLocale(locale: Locale): void {
  if (!isSupported(locale)) return;
  // Record the pick even when it matches the current locale: the visitor has now
  // confirmed a language, which must outrank a deal's default. Bailing out early
  // here would silently drop that (e.g. picking EN while EN is only a fallback).
  source = 'user';
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* ignore */
  }
  if (locale === currentLocale) return;
  currentLocale = locale;
  syncDocumentLang(locale);
  notify();
}

/**
 * Apply a deal's default language. Only takes effect when we have no locale
 * signal for this visitor at all — an explicit choice and the device's own
 * language both outrank it. Not persisted, so the user's switch still wins.
 */
export function applyDealDefaultLocale(locale?: string | null): void {
  if (source !== 'fallback' || !locale || !isSupported(locale) || locale === currentLocale) return;
  currentLocale = locale;
  syncDocumentLang(locale);
  notify();
}

/**
 * Whether the active locale is the visitor's own stated choice (?lang=, a saved
 * pick, or the switcher) rather than one we derived from their device or a deal.
 * Only a stated choice is safe to stamp into a generated link: writing a derived
 * locale into one turns it into an explicit choice the visitor never made, and
 * clicking that link pins it permanently.
 */
export function isUserChosenLocale(): boolean {
  return source === 'user';
}

/** Subscribe to locale changes; returns an unsubscribe fn. */
export function subscribeLocale(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Re-renders the calling component whenever the locale changes. */
export function useLocale(): Locale {
  const [, force] = useState(0);
  useEffect(() => subscribeLocale(() => force((n) => n + 1)), []);
  return currentLocale;
}

function getNestedValue(obj: any, path: string): string | undefined {
  const value = path.split('.').reduce((current, key) => current?.[key], obj);
  return typeof value === 'string' ? value : undefined;
}

export function t(key: TranslationKey, vars?: Record<string, string | number>): string {
  // Active locale first, fall back to English for any missing key, then the key itself.
  const value = getNestedValue(dictionaries[currentLocale], key) ?? getNestedValue(en, key);
  if (!value) return key;
  if (!vars) return value;
  return value.replace(/\{(\w+)\}/g, (_, name) => String(vars[name] ?? `{${name}}`));
}
